import { useEffect } from 'react';
import { FormLayout, Modal, TextField, Text, Form, Select, Checkbox, BlockStack, Divider } from '@shopify/polaris';
import { useState, useCallback } from 'react';
import { lengthLessThan, lengthMoreThan, notEmptyString, useField, useForm } from '@shopify/react-form';
import __ from 'languages/index';
import { useCreateStock, useMutateGetStock, useUpdateStock } from 'queries/stock.query';
import QuickSearchPaymentProfile from 'components/quickSearchPaymentProfile';
import { useGetProvinces, useGetWards } from 'queries/geographic.query';
export default function StockCreateModal({
  onClose,
  current_stock_id,
  show
}: {
  onClose: () => void;
  current_stock_id: string;
  show: boolean;
}) {
  /** Địa chính mới */
  const {
    mutateAsync: getProvinces
  } = useGetProvinces();
  const {
    mutateAsync: getWards
  } = useGetWards();
  const {
    mutate: getEntity,
    data: entity,
    isPending: loading
  } = useMutateGetStock();
  const {
    mutateAsync: updateEntity,
    isPending: updating,
    isSuccess: updateSuccess
  } = useUpdateStock();
  const {
    mutateAsync: createEntity,
    isSuccess: createSuccess
  } = useCreateStock();
  useEffect(() => {
    if (current_stock_id) {
      getEntity(current_stock_id);
    }
  }, [current_stock_id]);

  /**
   * Lấy URL ra... để query. chú ý phải load nội dung mới
   * trong useEffect
   */

  const useFields = {
    stock_id: useField<string>({
      value: entity?.stock_id ?? '',
      validates: []
    }),
    stock_name: useField<string>({
      value: entity?.stock_name ?? '',
      validates: [lengthLessThan(150, 'Không được dài hơn 150 ký tự.'), lengthMoreThan(1, 'Tên phải dài hơn 1 ký tự.'), notEmptyString('Trường này không được để trống.')]
    }),
    stock_code: useField<string>({
      value: entity?.stock_code ?? '',
      validates: [lengthLessThan(150, 'Không được dài hơn 150 ký tự.'), lengthMoreThan(1, 'Tên phải dài hơn 1 ký tự.'), notEmptyString('Trường này không được để trống.'), inputValue => {
        if (inputValue.match(/[^a-zA-Z0-9_-]+/gi)) return 'Yêu cầu không sử dụng ký tự đặc biệt.';
      }]
    }),
    stock_address: useField<string>({
      value: entity?.stock_address ?? '',
      validates: [lengthLessThan(255, 'No more than 255 characters.'), notEmptyString('Trường này không được để trống.')]
    }),
    manager: useField<string>({
      value: entity?.manager ?? '',
      validates: [notEmptyString('Trường này không được để trống.'), lengthLessThan(50, 'No more than 50 characters.')]
    }),
    hotline: useField<string>({
      value: entity?.hotline ?? '',
      validates: [inputValue => {
        if (!inputValue) return 'Yêu cầu phải có số điện thoại';
        if (inputValue.match(/\D+/gi)) return 'Phải là số điện thoại hợp lệ.';
      }, lengthLessThan(12, 'No more than 12 characters.'), notEmptyString('Trường này không được để trống.')]
    }),
    stock_address_city: useField<string>({
      value: entity?.stock_address_city ?? '',
      validates: [notEmptyString('Trường này không được để trống.')]
    }),
    stock_address_ward: useField<string>({
      value: entity?.stock_address_ward ?? '',
      validates: [notEmptyString('Trường này không được để trống.')]
    }),
    is_default: useField<number>({
      value: entity?.is_default ?? 0,
      validates: []
    }),
    /* Hỗ trợ việc cài đặt kho  */
    payment_profile_id: useField<string>({
      value: String(entity?.payment_profile_id || ''),
      validates: []
    })
  };
  const {
    fields,
    submit,
    submitting,
    dirty,
    reset: resetForms,
    submitErrors,
    makeClean
  } = useForm({
    fields: useFields,
    async onSubmit(values) {
      try {
        // create new
        if (!values.stock_id) {
          await createEntity({
            stock_name: values.stock_name,
            stock_code: values.stock_code,
            stock_address: values.stock_address,
            manager: values.manager,
            hotline: values.hotline,
            stock_address_city: values.stock_address_city,
            stock_address_ward: values.stock_address_ward,
            payment_profile_id: values.payment_profile_id || undefined,
            is_default: Number(values.is_default)
          });
        } else {
          await updateEntity({
            stock_id: values.stock_id,
            stock_name: values.stock_name,
            stock_code: values.stock_code,
            stock_address: values.stock_address,
            manager: values.manager,
            hotline: values.hotline,
            stock_address_city: values.stock_address_city,
            stock_address_ward: values.stock_address_ward,
            payment_profile_id: values.payment_profile_id || undefined,
            is_default: Number(values.is_default)
          });
        }
      } catch (e: any) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
      }
      return {
        status: 'success'
      };
    }
  });
  useEffect(() => {
    if (updateSuccess) onCloseModal();
  }, [updateSuccess]);
  useEffect(() => {
    if (createSuccess) onCloseModal();
  }, [createSuccess]);
  useEffect(() => {
    resetForms();
    makeClean();
  }, [show]);
  function onCloseModal() {
    resetForms();
    onClose();
  }

  /**** Địa chỉ mới */

  /**
   * Geographic selectors via API (replacing diachinh.json)
   */
  const [provinceOptions, setProvinceOptions] = useState<{
    label: string;
    value: string;
  }[]>([]);
  const [wardOptions, setWardOptions] = useState<{
    label: string;
    value: string;
  }[]>([]);
  useEffect(() => {
    getProvinces({}).then(res => {
      setProvinceOptions(res.map(p => ({
        label: p.name,
        value: p.code
      })));
    }).catch();
  }, []);

  /** Tối ưu */
  useEffect(() => {
    const cityCode = fields.stock_address_city.value;
    if (!cityCode) {
      setWardOptions([]);
      return;
    }
    getWards({
      parent_code: cityCode,
      limit: 200
    }).then(res => {
      const options = res.map(w => ({
        label: w.name,
        value: String(w.code)
      }));
      setWardOptions(options);

      // Nếu form đã có ward value nhưng wardOptions chưa chứa nó
      // thì giữ lại ward cũ (ví dụ khi load từ entity)
      const wardValue = fields.stock_address_ward.value;
      if (wardValue && !options.find(o => o.value === wardValue)) {
        fields.stock_address_ward.onChange('');
      }
    }).catch(

      /* ... */);
  }, [fields.stock_address_city.value]);

  /** END Tối ưu */

  return <Modal open={show} onClose={onCloseModal} title={current_stock_id ? 'Chỉnh sửa kho hàng' : 'Tạo kho hàng'} primaryAction={{
    content: current_stock_id ? __('update_label_button') : 'Tạo ngay',
    disabled: loading,
    loading: loading || updating,
    onAction: submit
  }} secondaryActions={[{
    content: 'Hủy',
    onAction: onCloseModal
  }]}>
      <Modal.Section>
        <Form onSubmit={() => {
        submit();
      }}>
          <BlockStack gap="400">
            <FormLayout.Group condensed>
              <TextField autoFocus autoComplete="off" requiredIndicator label="Tên kho" {...fields.stock_name} />
              <TextField autoComplete="off" requiredIndicator label="Mã kho" {...fields.stock_code} />
            </FormLayout.Group>

            <TextField label="Địa chỉ" requiredIndicator autoComplete="off" {...fields.stock_address} />

            <FormLayout.Group condensed>
              <Select label="Tỉnh/Thành phố" options={[...[{
              label: '==Chọn tỉnh thành==',
              value: ''
            }], ...provinceOptions]} value={fields.stock_address_city.value} onChange={val => fields.stock_address_city.onChange(val)} />
              <Select label="Xã/Thị trấn" options={wardOptions} value={fields.stock_address_ward.value} onChange={val => fields.stock_address_ward.onChange(val)} />
            </FormLayout.Group>

            <Divider />
            <Text as="h3" variant="headingSm">
              Thông tin người quản lý
            </Text>
            <Divider />

            <TextField label="Tên người quản lý" autoComplete="off" requiredIndicator {...fields.manager} />
            <TextField label="Hotline" autoComplete="off" requiredIndicator {...fields.hotline} />

            <Divider />
            <Text as="h3" variant="headingSm">
              Thông tin thanh toán
            </Text>
            <Divider />
            <QuickSearchPaymentProfile current_payment_profile_id={String(entity?.payment_profile_id || '')} onClose={v => fields.payment_profile_id.onChange(v.id)} title={'Chọn payment profile'} />
            <Checkbox label="Đặt làm kho mặc định" checked={useFields.is_default.value === 1} onChange={e => useFields.is_default.onChange(Number(e))} />
          </BlockStack>
        </Form>
      </Modal.Section>
    </Modal>;
}